import React, { useCallback, useState } from 'react';
import { endOfWeek, startOfWeek } from 'date-fns';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import {
  classifySageFailure,
  runSagePayroll,
  SAGE_FAILURE,
  toApiDate,
} from '../../../../../datahub/sage/sageApi';
import DatePickerField from '../../../../components/DatePicker/DatePickerField';
import selectUser from '../../../../store/selectors/appSelector';
import { failureMessage } from '../../../../utils/sageMessages';

// Sunday through Saturday, the same week the timekeeping grid pays against. Resolved on open
// rather than at module load so a tab left up overnight does not offer yesterday's period.
const currentPayPeriod = () => {
  const today = new Date();
  return { start: startOfWeek(today), end: endOfWeek(today) };
};

const SagePayrollButton = ({ sx }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const sessionUser = useSelector(selectUser);

  const [isOpen, setIsOpen] = useState(false);
  const [periodStart, setPeriodStart] = useState(() => currentPayPeriod().start);
  const [periodEnd, setPeriodEnd] = useState(() => currentPayPeriod().end);
  const [isRunning, setIsRunning] = useState(false);
  const [validationMessage, setValidationMessage] = useState(null);
  const [failure, setFailure] = useState(null);
  const [result, setResult] = useState(null);

  const handleOpen = useCallback(() => {
    const period = currentPayPeriod();
    setPeriodStart(period.start);
    setPeriodEnd(period.end);
    setValidationMessage(null);
    setFailure(null);
    setResult(null);
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    if (isRunning) {
      return;
    }
    setIsOpen(false);
    setValidationMessage(null);
    setFailure(null);
    setResult(null);
  }, [isRunning]);

  const handleSubmit = useCallback(() => {
    if (isRunning) {
      return;
    }
    const start = toApiDate(periodStart);
    const end = toApiDate(periodEnd);
    if (!start || !end) {
      setValidationMessage(intl.formatMessage({ id: 'sage.payroll.datesRequired' }));
      return;
    }
    if (end < start) {
      setValidationMessage(intl.formatMessage({ id: 'sage.payroll.invalidRange' }));
      return;
    }
    setValidationMessage(null);
    setFailure(null);
    setResult(null);
    setIsRunning(true);
    runSagePayroll({
      periodStart: start,
      periodEnd: end,
      dispatch,
      token: sessionUser?.token,
      refreshToken: sessionUser?.refreshToken,
    })
      .then((data) => {
        setResult(data);
      })
      .catch((error) => {
        setFailure(classifySageFailure(error));
      })
      .finally(() => {
        setIsRunning(false);
      });
  }, [
    isRunning,
    periodStart,
    periodEnd,
    intl,
    dispatch,
    sessionUser?.token,
    sessionUser?.refreshToken,
  ]);

  return (
    <>
      <Button variant="outlined" sx={sx} onClick={handleOpen}>
        {intl.formatMessage({ id: 'sage.payroll.button' })}
      </Button>
      <Dialog
        open={isOpen}
        onClose={handleClose}
        disableEscapeKeyDown={isRunning}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{intl.formatMessage({ id: 'sage.payroll.dialog.title' })}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <DatePickerField
              label={intl.formatMessage({ id: 'sage.payroll.periodStart' })}
              name="periodStart"
              value={periodStart}
              onChange={setPeriodStart}
              readOnly={isRunning}
              fullWidth
              testId="sage-payroll-period-start"
            />
            <DatePickerField
              label={intl.formatMessage({ id: 'sage.payroll.periodEnd' })}
              name="periodEnd"
              value={periodEnd}
              onChange={setPeriodEnd}
              readOnly={isRunning}
              helperText={intl.formatMessage({ id: 'sage.payroll.periodEndHelp' })}
              fullWidth
              testId="sage-payroll-period-end"
            />
            {isRunning && (
              <Typography variant="body2">
                {intl.formatMessage({ id: 'sage.payroll.inProgress' })}
              </Typography>
            )}
            {validationMessage && (
              <Typography variant="body2" color="error.main">
                {validationMessage}
              </Typography>
            )}
            {failure && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="error.main">
                  {failureMessage(intl, failure)}
                </Typography>
                {failure.kind === SAGE_FAILURE.UNAVAILABLE && (
                  <Button size="small" onClick={handleSubmit} sx={{ textTransform: 'none' }}>
                    {intl.formatMessage({ id: 'sage.retry' })}
                  </Button>
                )}
              </Box>
            )}
            {result && (
              <Box>
                <Typography variant="body2">
                  {intl.formatMessage(
                    { id: 'sage.payroll.success' },
                    {
                      status: result.status,
                      payLines: result.payLinesProcessed,
                      corporations: result.corporations,
                      placements: result.placementsProcessed,
                    }
                  )}
                </Typography>
                {result.placementsSkippedUnverified > 0 && (
                  <Typography variant="body2">
                    {intl.formatMessage(
                      { id: 'sage.payroll.skippedUnverified' },
                      { count: result.placementsSkippedUnverified }
                    )}
                  </Typography>
                )}
                <Typography variant="caption">
                  {intl.formatMessage(
                    { id: 'sage.payroll.runId' },
                    { runId: result.runId, sageBatchId: result.sageBatchId }
                  )}
                </Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isRunning} sx={{ textTransform: 'none' }}>
            {intl.formatMessage({ id: 'button.cancel' })}
          </Button>
          <LoadingButton
            variant="contained"
            loading={isRunning}
            onClick={handleSubmit}
            sx={{ textTransform: 'none' }}
          >
            {intl.formatMessage({ id: 'sage.payroll.submit' })}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

SagePayrollButton.propTypes = {
  sx: PropTypes.shape({}),
};

export default SagePayrollButton;
